import React, { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { DataSource } from 'components/DataSource';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import useApp from 'hooks/useApp';
import useLocalStore from 'hooks/useLocalStore';

export const CompanySelect = () => {
	const localStore = useLocalStore();
	const app = useApp();
	const { empid, setempid } = useApp();
	const [empresaID, setEmpresa] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		setEmpresa(empid);
	}, [empid]);

	const handleChange = (event) => {
		setEmpresa(event.value);
	};

	const handleSelectCompany = async () => {
		if (empresaID == null) return;

		setSubmitting(true);
		try {
			setempid(empresaID);
			localStore.setTenancy(empresaID);
			app.flashMsg("Empresa alterada com sucesso!", "success");
			app.closeDialogs();
		} catch (error) {
			console.error("Erro ao selecionar empresa:", error);
			app.flashMsg("Erro ao alterar a empresa.", "error");
		} finally {
			setSubmitting(false);
		}
	};

	// Template customizado para itens da lista
	const companyOptionTemplate = (option) => {
		if (!option) return null;
		const isSelected = String(option.value) === String(empresaID);

		return (
			<div className={`flex align-items-center justify-content-between p-2 w-full border-round transition-duration-150 ${isSelected ? 'surface-hover font-semibold' : ''}`}>
				<div className="flex align-items-center gap-2">
					<div className={`flex align-items-center justify-content-center border-round p-1 ${isSelected ? 'bg-primary text-white' : 'surface-200 text-600'}`} style={{ width: '28px', height: '28px' }}>
						<i className="pi pi-building text-sm" />
					</div>
					<span className={isSelected ? 'text-primary font-bold' : 'text-800'}>
						{option.label}
					</span>
				</div>
				{isSelected && (
					<i className="pi pi-check text-primary font-bold text-sm mr-1" />
				)}
			</div>
		);
	};

	// Template para o item selecionado exibido no campo
	const selectedCompanyTemplate = (option, props) => {
		if (option) {
			return (
				<div className="flex align-items-center gap-2">
					<i className="pi pi-building text-primary font-bold" />
					<span className="font-semibold text-900">{option.label}</span>
				</div>
			);
		}
		return <span className="text-500">{props.placeholder}</span>;
	};

	return (
		<div className="company-select-container">
			<style>{`
				.company-select-container {
					padding: 0.5rem 0.25rem;
				}
				.company-icon-badge {
					width: 44px;
					height: 44px;
					border-radius: 12px;
					background: linear-gradient(135deg, rgba(37, 99, 235, 0.2) 0%, rgba(59, 130, 246, 0.35) 100%);
					border: 1px solid rgba(59, 130, 246, 0.4);
					color: #3b82f6;
					display: flex;
					align-items: center;
					justify-content: center;
					box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15);
				}
				.company-info-card {
					background: rgba(37, 99, 235, 0.08);
					border: 1px solid rgba(37, 99, 235, 0.2);
					border-radius: 10px;
					padding: 0.85rem 1rem;
				}
				.company-dropdown-custom {
					border-radius: 8px !important;
					transition: all 0.2s ease-in-out;
				}
				.company-dropdown-custom:not(.p-disabled):hover {
					border-color: #3b82f6 !important;
				}
				.company-dropdown-custom.p-focus {
					border-color: #3b82f6 !important;
					box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25) !important;
				}
				.company-btn-confirm {
					background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
					border: none !important;
					font-weight: 600 !important;
					border-radius: 8px !important;
					box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35) !important;
					transition: all 0.2s ease;
					padding: 0.65rem 1.25rem !important;
				}
				.company-btn-confirm:not(:disabled):hover {
					background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%) !important;
					box-shadow: 0 6px 16px rgba(37, 99, 235, 0.5) !important;
					transform: translateY(-1px);
				}
				.company-btn-cancel {
					border-radius: 8px !important;
					font-weight: 500 !important;
					padding: 0.65rem 1rem !important;
				}
			`}</style>

			{/* Cabeçalho */}
			<div className="flex align-items-center gap-3 mb-3 pr-4">
				<div className="company-icon-badge flex-shrink-0">
					<i className="pi pi-building text-xl" />
				</div>
				<div>
					<h3 className="text-xl font-bold text-900 m-0 line-height-2">Trocar de Empresa</h3>
					<span className="text-xs text-500 font-medium">Selecione a unidade de trabalho ativa no sistema</span>
				</div>
			</div>

			{/* Cartão de Aviso / Instrução */}
			<div className="company-info-card flex align-items-start gap-3 mb-4">
				<i className="pi pi-info-circle text-primary text-lg mt-1 flex-shrink-0" />
				<div className="text-xs text-700 line-height-3 m-0">
					Ao alternar a empresa, os módulos, relatórios e filtros da sessão serão atualizados para o contexto da nova unidade selecionada.
				</div>
			</div>

			{/* Seletor de Empresa */}
			<div className="mb-4">
				<label htmlFor="company-dropdown-select" className="block text-sm font-semibold text-800 mb-2">
					Empresa de Trabalho
				</label>
				<DataSource apiPath="components_data/empid_option_list">
					{({ response, loading }) => {
						if (loading) {
							return (
								<div className="flex align-items-center justify-content-center p-4 gap-2 surface-100 border-round-lg border-1 surface-border">
									<ProgressSpinner style={{ width: '24px', height: '24px' }} strokeWidth="4" />
									<span className="text-sm text-600 font-medium">Carregando lista de empresas...</span>
								</div>
							);
						}

						const options = response || [];

						return (
							<Dropdown
								id="company-dropdown-select"
								className="w-full company-dropdown-custom"
								name="empid"
								optionLabel="label"
								optionValue="value"
								value={empresaID}
								onChange={handleChange}
								options={options}
								itemTemplate={companyOptionTemplate}
								valueTemplate={selectedCompanyTemplate}
								filter
								filterBy="label"
								placeholder="Pesquise ou selecione uma empresa..."
								emptyFilterMessage="Nenhuma empresa encontrada com este nome"
								emptyMessage="Nenhuma empresa disponível"
								showClear={false}
							/>
						);
					}}
				</DataSource>
			</div>

			{/* Ações do Rodapé */}
			<div className="flex justify-content-end align-items-center gap-2 pt-3 border-top-1 surface-border">
				<Button
					type="button"
					label="Cancelar"
					icon="pi pi-times"
					className="p-button-outlined p-button-secondary company-btn-cancel text-sm"
					onClick={() => app.closeDialogs()}
					disabled={submitting}
				/>
				<Button
					type="button"
					label={submitting ? "Alternando..." : "Confirmar Seleção"}
					icon={submitting ? "" : "pi pi-check"}
					loading={submitting}
					disabled={empresaID == null}
					onClick={handleSelectCompany}
					className="company-btn-confirm text-sm"
				/>
			</div>
		</div>
	);
};

export default CompanySelect;
